import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { Box } from '~/components/elements/Box'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { useSurveyForSettingsQueryData } from '~/hooks/useSurveyForSettingsQuery'

import { useDownloadAnswerFile } from '../hooks/useDownloadAnswerFile'
import { useDownloadQuestionFile } from '../hooks/useDownloadQuestionFile'
import { useMainQueryData } from '../hooks/useMainQuery'
import { useSurveyId } from '../hooks/useSurveyId'
import { useUploadAnswerFile } from '../hooks/useUploadAnswerFile'
import { useUploadQuestionFile } from '../hooks/useUploadQuestionFile'

import styles from './DataSettings.css'
import { DataSettingsCard } from './DataSettingsCard'
import { DownloadFileSection } from './DownloadFileSection'
import { UploadAnswerFileSection } from './UploadAnswerFileSection'
import { UploadedDateSection } from './UploadedDateSection'
import { UploadQuestionFileSection } from './UploadQuestionFileSection'

export const DataSettings = () => {
  const surveyId = useSurveyId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('settings'),
      createBreadcrumb('dataSettings', { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const uploadedFilesProperties = useMainQueryData()
  const survey = useSurveyForSettingsQueryData()

  const [uploadQuestionFile, isUploadQuestionFileExecuting] =
    useUploadQuestionFile()
  const downloadQuestionFile = useDownloadQuestionFile()
  const [uploadAnswerFile, isUploadAnswerFileExecuting] = useUploadAnswerFile()
  const downloadAnswerFile = useDownloadAnswerFile()

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      executing={isUploadQuestionFileExecuting || isUploadAnswerFileExecuting}
    >
      <Box className={styles.cards} verticalAppearance="stretch-scroll">
        <DataSettingsCard title={t('questionnaire_data')}>
          <UploadQuestionFileSection onSubmit={uploadQuestionFile} />

          <DownloadFileSection
            data={uploadedFilesProperties?.questionnaire}
            onDownload={downloadQuestionFile}
          />

          <UploadedDateSection
            dateString={survey?.questionnaireFileUploadedAt}
          />
        </DataSettingsCard>

        <DataSettingsCard title={t('response_data')}>
          <UploadAnswerFileSection onSubmit={uploadAnswerFile} />

          <DownloadFileSection
            data={uploadedFilesProperties?.answer}
            onDownload={downloadAnswerFile}
          />

          <UploadedDateSection dateString={survey?.answerFileUploadedAt} />
        </DataSettingsCard>
      </Box>
    </PageContainer>
  )
}
